import type { AccountRequirements } from '@identity/domain/types';

/**
 * The account fields the domain publishes: the two the wizard shows, plus the three the profile
 * screen reads.
 *
 * The last three are optional because an account may have no surname, no phone registered and no
 * password ever changed, and because a response from an older contract has to keep rendering. None
 * of them is normalised: an invalid date or a number that does not look like E.164 is an incident
 * worth seeing, not a value to quietly correct.
 */
export type AccountProfile = Readonly<{
  firstName: string;
  email: string;
  lastName?: string;
  phone?: string;
  passwordChangedAt?: string;
}>;

/**
 * The status of the account a link token gives access to. It reports what is still missing, never
 * what comes next: the order of the steps belongs to the front end.
 *
 * `enforcementMode` stays a plain `string` instead of the known union, because an absent value and
 * an unknown one are two different incidents and normalising either away hides both.
 */
export type AccountStatus = Readonly<{
  requirements: AccountRequirements;
  account: AccountProfile;
  enforcementMode?: string;
}>;

/**
 * Creates an AccountStatus, freezing both levels so the requirement map can only be replaced, never
 * changed in place.
 */
export const createAccountStatus = (props: {
  requirements: AccountRequirements;
  account: AccountProfile;
  enforcementMode?: string;
}): AccountStatus => {
  const { requirements, account, enforcementMode } = props;

  return Object.freeze({
    requirements: Object.freeze({ ...requirements }),
    account: Object.freeze({ ...account }),
    enforcementMode,
  });
};
